import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-jsonschema-form'
import RCTooltip from 'rc-tooltip'
import classnames from 'classnames'

import TenantActions from '../tenantActions/TenantActions'
import DateWidget from '../dateWidget/DateWidget'
import { updateFormUiSchema } from '../config'
import { scrollTop } from '../../../../../util'
import { memberLookupRequest } from '../../../../../services'
import ModalContainer from '../../../../common/modal'
import { beautifyLabel, decoratedWithAsteriskForRequired } from './util'
import { EditFormNotification } from './components/EditFormNotification'

const propTypes = {
  schema: PropTypes.object,
  formData: PropTypes.object,
  selectedTenant: PropTypes.string,
  tenantInfo: PropTypes.object,
  isCreating: PropTypes.bool,
  isSaving: PropTypes.bool,
  memberExpiringDateRange: PropTypes.object,
  serverErrors: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onMembersClick: PropTypes.func.isRequired,
}

class Edit extends Component {
  constructor(props) {
    super(props)
    this.token = props.token
    this.onSaveClick = this.onSaveClick.bind(this)
    this.onCancelClick = this.onCancelClick.bind(this)
    this.renderCustomFieldTemplate = this.renderCustomFieldTemplate.bind(this)
    this.onClickSearchMemberByEmail = this.onClickSearchMemberByEmail.bind(this)
    this.renderActionButtons = this.renderActionButtons.bind(this)
    this.validate = this.validate.bind(this)
    this.toggleWarning = this.toggleWarning.bind(this)

    this.state = {
      schema: null,
      uiSchema: null,
      formData: null,
      modal: {
        visible: false,
        type: 'edit',
        header: '',
        children: [],
      },
      warningMessage: '',
      messageTimeout: null,
    }
  }

  componentDidMount() {
    const formState = updateFormUiSchema({ ...this.props }, null)
    this.setState(formState)
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { isSaving } = this.props
    const switchingToSaving = !isSaving && nextProps.isSaving
    const stillSaving = isSaving && nextProps.isSaving
    return !(switchingToSaving || stillSaving)
  }

  onSaveClick(obj) {
    const { isCreating } = this.props
    const { acronym } = this.state.formData
    if (!isCreating && acronym.length > 10) {
      this.toggleWarning(
        'For your information: please note that acronyms for new tenants may be at most ten characters.' +
          'This restriction does not apply here since you are editing an existing one.',
      )
    }

    const { onSaveClick, isSaving } = this.props

    // call parent onSaveClick method
    if (!isSaving && typeof onSaveClick === 'function') {
      onSaveClick(obj.formData)
    }
  }

  onCancelClick(e) {
    const { onCancelClick, isSaving } = this.props

    // call parent onCancelClick method
    if (!isSaving && typeof onCancelClick === 'function') {
      onCancelClick()
    }
  }

  async onClickSearchMemberByEmail(e) {
    e.stopPropagation()
    e.preventDefault()

    const { token } = this.props
    const { members } = this.state.formData
    const memberGroup = e.target.closest('.field-object')
    const email = memberGroup.querySelector('input[label="email"]').value
    const isEmail = (s) =>
      !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(s)
    if (isEmail(email)) {
      this.setState((state) => {
        return {
          ...state,
          modal: {
            visible: true,
            type: 'edit',
            header: 'Email not valid',
            children: 'Please fill a valid email address.',
          },
        }
      })
    }

    const memberLookup = await memberLookupRequest.post({ email: email }, token)
    if (memberLookup?.email) {
      members.map((item) => {
        if (item.email.toLowerCase() === memberLookup.email.toLowerCase()) {
          Object.keys(memberLookup).forEach((prop) => {
            item[prop] = memberLookup[prop] !== '' ? memberLookup[prop] : null
          })
        }
      })

      const inputs = memberGroup.getElementsByTagName('input')
      for (const item of inputs) {
        if (memberLookup[item.getAttribute('label')]) {
          item.value = memberLookup[item.getAttribute('label')]
        }
      }
    } else {
      this.setState((actualState) => {
        return {
          ...actualState,
          modal: {
            visible: true,
            type: 'edit',
            header: 'Member was not found.',
            children: 'Please fill manually all member data.',
          },
        }
      })
    }

    return true
  }

  renderCustomFieldTemplate(obj) {
    const { id, classNames, label, help, rawDescription, errors, children } =
      obj
    const noTooltipLabels = ['Members', 'firstName', 'lastName', 'email']
    const disabledAlertMessage =
      'Note: If you need to change this item please contact Nebula Platform.'
    const disabledAlertMessageFields = [
      'Status',
      'Type',
      'Spring Cloud Services',
      'Platform Automation Enabled',
    ]

    return (
      <div className={classNames}>
        <div className="header">
          {!!rawDescription && !noTooltipLabels.includes(label) ? (
            <label
              className="control-label control-label--tooltip"
              htmlFor={id}
            >
              {beautifyLabel(label)}
              <RCTooltip
                placement="topLeft"
                trigger={['click']}
                destroyTooltipOnHide={true}
                prefixCls="tooltip-layer"
                overlay={rawDescription}
              >
                <div className="info-icon">&#8505;</div>
              </RCTooltip>
            </label>
          ) : (
            <label className="control-label" htmlFor={id}>
              {beautifyLabel(label)}
            </label>
          )}
        </div>
        {children}
        {label === 'email' && (
          <RCTooltip
            placement="topLeft"
            trigger={['hover', 'click', 'focus']}
            destroyTooltipOnHide={true}
            prefixCls="tooltip-layer"
            overlay="After filling the email field, please click here, if you want to automatically pre-fill all data member."
          >
            <a
              href="#"
              onClick={(e) => this.onClickSearchMemberByEmail(e)}
              className="btn searchBtn"
            >
              <wb-icon
                className="wb-color-highlight"
                name="user"
                style={{ width: '15px', color: '#00adef' }}
              />
            </a>
          </RCTooltip>
        )}
        {errors}
        {help}
        {obj.disabled && disabledAlertMessageFields.includes(label) && (
          <div className="disabledMessage">{disabledAlertMessage}</div>
        )}
      </div>
    )
  }

  transformErrors(errors) {
    return errors.map((err) => {
      if (err.message === 'should match format "email"') {
        err.message = 'invalid email format'
      } else if (
        err.message === 'should match pattern "^[\\+0-9\\s\\-\\(\\)]*$"'
      ) {
        err.message = 'invalid phone number format'
      }
      return err
    })
  }

  renderCustomErrorList(obj) {
    const { errors } = obj

    return (
      <div className="tenants-edit__errors">
        Please fix the {errors.length} error(s) in the form below before Saving.
      </div>
    )
  }

  renderActionButtons(pos) {
    const { isSaving } = this.props
    return (
      <div
        className={`tenants-edit__actions tenants-edit__actions--${pos} tenant-actions`}
      >
        <div className="spacer" />
        <button
          type="button"
          disabled={isSaving}
          aria-label="Cancel editing Tenant"
          className="wb-button wb-button--theme-dark wb-button--secondary wb-button--small wb-button--icon-only close"
          onClick={this.onCancelClick}
        >
          <wb-icon name="close" aria-hidden="true" class="wb-button__icon" />
        </button>
        <button
          type="submit"
          disabled={isSaving}
          aria-label="Edit Tenant"
          className="wb-button wb-button--theme-dark wb-button--primary wb-button--small wb-button--icon-only save"
        >
          <wb-icon name="save" aria-hidden="true" class="wb-button__icon" />
        </button>
      </div>
    )
  }

  toggleWarning(message) {
    clearTimeout(this.state.messageTimeout)
    this.setState({
      warningMessage: message || '',
    })
    if (message) {
      this.setState({
        messageTimeout: setTimeout(() => {
          this.setState({
            warningMessage: '',
          })
        }, 10000),
      })
    }
  }

  validate(formData, errors) {
    const { isCreating } = this.props

    const acronymLength = formData.acronym?.length || 0
    if (acronymLength === 0) {
      errors.acronym.addError('Acronym is required')
    }
    if (isCreating && acronymLength > 10) {
      errors.acronym.addError('Acronym should be at most ten characters')
    }

    if (formData.description === '' || formData.description === null) {
      errors.description.addError('Description is required')
    }

    if (formData.docLink) {
      const urlPattern =
        /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/
      const isValid = urlPattern.test(formData.docLink)
      if (!isValid) {
        errors.docLink.addError('Invalid Document Url')
      }
    }

    if (formData.coNumber) {
      formData.coNumber = formData.coNumber.replace(/ /g, '').toLowerCase()
      if (formData.coNumber === '') {
        errors.coNumber.addError('Cost Center / CO Number is required')
      }
    }

    if (!formData.programName) {
      errors.programName.addError('Program name is required field')
    }

    if (!isCreating) {
      const { costNumber, costCenter } =
        formData.nebulaControllingInformation || {}
      if (!(costNumber && costCenter)) {
        errors.nebulaControllingInformation.addError(
          'Cost number and cost center are required and need to be specified separately now',
        )
      }
    }
    console.log(errors)
    return errors
  }

  render() {
    const { selectedTenant, isSaving, serverErrors, formData, onMembersClick } =
      this.props
    const { modal } = this.state
    const widgets = { dateWidget: DateWidget }

    const tenantInfo = { ...this.state.formData }
    const tenantsEditClassNames = classnames('tenants-edit tenants-info', {
      'tenants-edit--saving': isSaving,
      'tenants-edit--serverErrors': serverErrors.hasErrors,
    })

    const onFormFieldChange = (data) => {
      const { formData: currFormData } = data
      const formState = updateFormUiSchema({ ...this.props }, currFormData)
      this.setState(formState)
    }

    return (
      <div className="tenant-content">
        <TenantActions
          position="top"
          title={selectedTenant}
          provider={tenantInfo.providerSlug}
          isEditing={true}
          onMembersClick={onMembersClick}
        />
        <table className={tenantsEditClassNames}>
          <tbody className="tenants-edit__content">
            <tr>
              <td colSpan="4">
                {serverErrors.hasErrors && (
                  <div className="tenants-edit__errors tenants-edit__errors--server">
                    {serverErrors.message}
                  </div>
                )}
                <div className="tenants-edit__required-info">
                  Fields marked with an asterisk (*) are required
                </div>
                <Form
                  className="tenants-edit__form"
                  noHtml5Validate={true}
                  validate={this.validate}
                  disabled={isSaving}
                  schema={{
                    ...decoratedWithAsteriskForRequired(this.state.schema),
                  }}
                  uiSchema={{ ...this.state.uiSchema }}
                  formData={{ ...tenantInfo }}
                  FieldTemplate={this.renderCustomFieldTemplate}
                  widgets={widgets}
                  transformErrors={this.transformErrors}
                  ErrorList={this.renderCustomErrorList}
                  onError={scrollTop}
                  onChange={onFormFieldChange.bind(this)}
                  onSubmit={this.onSaveClick}
                >
                  {this.renderActionButtons('top')}
                  {this.renderActionButtons('bottom')}
                </Form>
                {modal && modal.visible && (
                  <ModalContainer
                    type={modal.type}
                    header={modal.header}
                    children={modal.children}
                    onCloseButton={() => this.setState({ modal: false })}
                    onCloseModalBoxClick={() => this.setState({ modal: false })}
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
        {this.state.warningMessage && (
          <EditFormNotification
            visible={true}
            message={this.state.warningMessage}
            onDismiss={() => {
              this.toggleWarning('')
            }}
          />
        )}
      </div>
    )
  }
}

Edit.propTypes = propTypes

export default Edit
